// RepKeypad — T-095 §2. A bottom-sheet numeric keypad for entering
// the rep count after a set. The 64 px keys are big enough for a
// wet thumb on a 360 px screen, identical to the existing ±1
// controls, and never zoom the page the way the OS keyboard does
// on iOS. Escape and the Cancel button restore the value from
// before the sheet opened. The keypad is the only direct path to a
// number (T-098 removed voice control).

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { tick as audioTick } from "../../services/audioCues";

const MAX_REPS = 999;

export interface RepKeypadProps {
  /** The current value the person is editing from. */
  initialValue: number;
  /** Called whenever the buffer changes. The big number behind
   *  the sheet updates live. */
  onChange: (value: number) => void;
  /** Called when the person confirms (Set-done key). */
  onConfirm: (value: number) => void;
  /** Called when the person cancels (Cancel button, Escape,
   *  backdrop tap). The parent restores `initialValue`. */
  onCancel: () => void;
}

export function RepKeypad({
  initialValue,
  onChange,
  onConfirm,
  onCancel,
}: RepKeypadProps): JSX.Element {
  const { t } = useI18n();
  const [buffer, setBuffer] = useState<string>(
    initialValue > 0 ? String(initialValue) : "",
  );
  const containerRef = useRef<HTMLDivElement>(null);
  // The big number behind the sheet updates live. Use a ref so
  // the effect is stable across parent re-renders — otherwise a
  // new `onChange` reference each render would replay the buffer
  // value into the parent and fight the parent’s own state.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current(buffer === "" ? 0 : Number(buffer));
  }, [buffer]);

  // Escape cancels, focus the first key on open, return focus to
  // the trigger on close (handled by the parent via onCancel).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        appendDigit(e.key);
      } else if (e.key === "Backspace") {
        backspace();
      } else if (e.key === "Enter") {
        confirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer]);

  const appendDigit = (d: string) => {
    audioTick();
    setBuffer((prev) => {
      if (prev.length >= 3) return prev; // cap at 999; ignore a fourth
      const next = (prev + d).replace(/^0+(\d)/, "$1"); // drop leading zeros
      return next;
    });
  };

  const backspace = () => {
    audioTick();
    setBuffer((prev) => prev.slice(0, -1));
  };

  const confirm = () => {
    const n = buffer === "" ? 0 : Number(buffer);
    if (n <= 0) return; // disabled at 0/empty
    onConfirm(n);
  };

  const value = buffer === "" ? 0 : Number(buffer);
  const keys: Array<{ label: string; testid: string; onClick: () => void; variant: "digit" | "back" | "ok" }> = [
    { label: "1", testid: "key-1", onClick: () => appendDigit("1"), variant: "digit" },
    { label: "2", testid: "key-2", onClick: () => appendDigit("2"), variant: "digit" },
    { label: "3", testid: "key-3", onClick: () => appendDigit("3"), variant: "digit" },
    { label: "4", testid: "key-4", onClick: () => appendDigit("4"), variant: "digit" },
    { label: "5", testid: "key-5", onClick: () => appendDigit("5"), variant: "digit" },
    { label: "6", testid: "key-6", onClick: () => appendDigit("6"), variant: "digit" },
    { label: "7", testid: "key-7", onClick: () => appendDigit("7"), variant: "digit" },
    { label: "8", testid: "key-8", onClick: () => appendDigit("8"), variant: "digit" },
    { label: "9", testid: "key-9", onClick: () => appendDigit("9"), variant: "digit" },
    { label: "⌫", testid: "key-back", onClick: backspace, variant: "back" },
    { label: "0", testid: "key-0", onClick: () => appendDigit("0"), variant: "digit" },
    { label: "✓", testid: "keypad-done", onClick: confirm, variant: "ok" },
  ];

  return (
    <div
      ref={containerRef}
      data-testid="rep-keypad"
      role="dialog"
      aria-modal="true"
      aria-label={t("workoutRunner.keypad.title")}
      className="fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-[640px] flex-col items-stretch gap-3 border border-[var(--color-line)] bg-[var(--color-slab)] p-4 shadow-2xl"
      style={{ borderRadius: "var(--radius-card) var(--radius-card) 0 0" }}
    >
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg uppercase tracking-[0.06em] text-[var(--color-ink-hi)]">
          {t("workoutRunner.keypad.title")}
        </h2>
        <p
          className="font-numeric tabular text-[var(--color-ink-hi)]"
          style={{ fontSize: 36, lineHeight: 1, fontWeight: 700, minWidth: "3ch", textAlign: "right" }}
          aria-live="polite"
          data-testid="keypad-preview"
        >
          {value > 0 ? value : <span style={{ opacity: 0.4 }}>—</span>}
        </p>
      </div>
      <div
        className="grid grid-cols-3 gap-2"
        data-testid="keypad-grid"
      >
        {keys.map((k) => {
          const isOk = k.variant === "ok";
          const isBack = k.variant === "back";
          const disabled = isOk && value <= 0;
          return (
            <button
              key={k.testid}
              type="button"
              data-testid={k.testid}
              aria-label={
                isBack
                  ? t("workoutRunner.keypad.backspace")
                  : isOk
                    ? t("workoutRunner.keypad.confirm")
                    : k.label
              }
              onClick={k.onClick}
              disabled={disabled}
              className={
                "border-2 font-display transition-all active:scale-95 " +
                (isOk
                  ? "border-[var(--color-lime)] bg-[var(--color-lime)] text-[var(--color-void)] disabled:cursor-not-allowed disabled:opacity-40"
                  : isBack
                    ? "border-[var(--color-line-strong)] bg-[var(--color-slab-2)] text-[var(--color-ink)]"
                    : "border-[var(--color-line-strong)] bg-[var(--color-slab)] text-[var(--color-ink-hi)] hover:border-[var(--color-lime)]")
              }
              style={{ minHeight: "64px", minWidth: "64px", borderRadius: "var(--radius-sharp)", fontSize: 28 }}
            >
              {k.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        data-testid="keypad-cancel"
        onClick={onCancel}
        className="border border-[var(--color-line-strong)] px-3 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
        style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
      >
        {t("common.cancel")}
      </button>
    </div>
  );
}

void MAX_REPS;